import { Navigation, MapPin, Compass, Clock } from "lucide-react";
import { useI18n } from "@/lib/i18n";

export default function PhoneMockup() {
  const { t, lang } = useI18n();
  const isAr = lang === "ar";
  return (
    <div className="relative mx-auto w-[300px] md:w-[340px]" data-testid="hero-phone-mockup">
      {/* Floating labels */}
      <div className={`hidden md:flex absolute -top-4 ${isAr ? "-right-16" : "-left-16"} items-center gap-2 rounded-full bg-white shadow-soft border border-brand-line px-3 py-1.5 text-xs font-semibold text-brand-green animate-float`}>
        <MapPin className="h-3.5 w-3.5" />
        {isAr ? "تم الحفظ" : "Saved"}
      </div>
      <div className={`hidden md:flex absolute bottom-16 ${isAr ? "-left-14" : "-right-14"} items-center gap-2 rounded-full bg-brand-charcoal text-white shadow-lift px-3 py-1.5 text-xs font-semibold animate-float`} style={{ animationDelay: "1s" }}>
        <Clock className="h-3.5 w-3.5 text-brand-gold" />
        18:00
      </div>

      {/* Phone frame */}
      <div className="relative rounded-[2.5rem] border-[10px] border-brand-charcoal bg-brand-charcoal shadow-lift">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-6 w-28 bg-brand-charcoal rounded-b-2xl z-20" />
        <div className="relative overflow-hidden rounded-[1.75rem] bg-white h-[560px] md:h-[600px]">
          {/* Status bar */}
          <div className="flex items-center justify-between px-6 pt-4 pb-1 text-[10px] font-semibold text-brand-charcoal">
            <span>9:41</span>
            <span className="flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-green" />
              <span className="h-1.5 w-1.5 rounded-full bg-brand-green" />
              <span className="h-1.5 w-1.5 rounded-full bg-brand-green" />
            </span>
          </div>

          {/* App header */}
          <div className="px-5 pt-4 pb-3 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-brand-green font-semibold">
                {isAr ? "ركنتي" : "Raknati"}
              </div>
              <div className="text-sm font-bold text-brand-charcoal">
                {t("hero.mock.parked")}
              </div>
            </div>
            <div className="h-8 w-8 rounded-full bg-brand-greenSoft flex items-center justify-center">
              <Compass className="h-4 w-4 text-brand-green" />
            </div>
          </div>

          {/* Map visual */}
          <div className="relative mx-4 h-40 rounded-2xl overflow-hidden border border-brand-line bg-brand-sand">
            <div className="absolute inset-0 grid-lines opacity-70" />
            {/* Roads */}
            <div className="absolute left-6 top-6 bottom-6 w-1.5 bg-white/90 rounded-full" />
            <div className="absolute top-1/2 left-3 right-3 h-1.5 bg-white/90 rounded-full -translate-y-1/2" />
            {/* Path */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 300 160">
              <path d="M40,140 C 90,110 130,80 180,60 L 220,50" stroke="#00732F" strokeWidth="3" strokeDasharray="4 4" fill="none" strokeLinecap="round" />
            </svg>
            {/* User */}
            <div className="absolute bottom-4 left-6 flex flex-col items-center">
              <div className="h-3 w-3 rounded-full bg-brand-charcoal ring-4 ring-brand-charcoal/15" />
            </div>
            {/* Car pin */}
            <div className="absolute top-6 right-8 flex flex-col items-center">
              <div className="h-6 w-6 rounded-full bg-brand-red text-white flex items-center justify-center shadow-lift">
                <MapPin className="h-3.5 w-3.5" />
              </div>
              <div className="mt-1 text-[9px] font-bold text-brand-charcoal bg-white rounded-full px-1.5 py-0.5 shadow-soft">
                P3 · F-318
              </div>
            </div>
          </div>

          {/* Location card */}
          <div className="mx-4 mt-4 rounded-2xl border border-brand-line p-3 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-brand-charcoal/50 font-semibold">
                {t("hero.mock.level")} · {t("hero.mock.zone")}
              </div>
              <div className="text-sm font-bold text-brand-charcoal mt-0.5">
                {t("hero.mock.bay")}
              </div>
              <div className="text-[10px] text-brand-charcoal/60 mt-1">
                {t("hero.mock.saved")}
              </div>
            </div>
            <div className="text-end">
              <div className="text-[10px] uppercase tracking-[0.18em] text-brand-green font-semibold">
                {isAr ? "المشي" : "Walk"}
              </div>
              <div className="text-sm font-bold text-brand-charcoal mt-0.5">
                {t("hero.mock.walk")}
              </div>
            </div>
          </div>

          {/* Chips */}
          <div className="mx-4 mt-3 flex flex-wrap gap-1.5">
            {[
              isAr ? "بلوتوث" : "Bluetooth",
              isAr ? "صور" : "Photos",
              isAr ? "بوصلة" : "Compass",
              isAr ? "منبه" : "Reminder",
            ].map((c) => (
              <span key={c} className="text-[10px] px-2 py-1 rounded-full bg-brand-sand text-brand-charcoal/75 border border-brand-line font-medium">
                {c}
              </span>
            ))}
          </div>

          {/* Find CTA */}
          <div className="absolute inset-x-4 bottom-6">
            <button className="w-full rounded-full bg-brand-green text-white py-3 text-sm font-semibold shadow-soft flex items-center justify-center gap-2" data-testid="mock-find-btn" aria-hidden="true" tabIndex={-1}>
              <Navigation className="h-4 w-4" />
              {t("hero.mock.find")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
